/**
 * Utility functions for consistent error handling across authentication flows
 */

/**
 * Converts Supabase/auth errors into user-friendly messages
 * Based on the error handling approach used in LoginPage and SignUpPage
 * 
 * @param {Error} err - The error object from Supabase/auth
 * @param {Object} options - Options for error handling
 * @param {boolean} options.hideAccountExistence - If true, never reveal if account exists (for forgot password)
 * @param {boolean} options.isPasswordReset - If true, prioritize session/token errors (for reset password)
 * @returns {string} - A user-friendly error message
 */
export const getFriendlyErrorMessage = (err, options = {}) => {
    const { hideAccountExistence = false, isPasswordReset = false } = options;
    if (!err) {
        return "Something went wrong. Please try again.";
    }

    const normalizedMessage = err.message?.toLowerCase() || "";
    const status = err.status || err.statusCode;
    const errorCode = err.code?.toLowerCase() || "";

    // Email not confirmed / needs verification - CHECK THIS FIRST before invalid credentials
    // Supabase returns various formats for email verification errors
    if (
        normalizedMessage.includes("email not confirmed") ||
        normalizedMessage.includes("email_not_confirmed") ||
        normalizedMessage.includes("confirmation required") ||
        normalizedMessage.includes("verify email") ||
        normalizedMessage.includes("email verification") ||
        normalizedMessage.includes("email needs to be verified") ||
        normalizedMessage.includes("email must be verified") ||
        normalizedMessage.includes("unverified email") ||
        normalizedMessage.includes("email is not verified") ||
        normalizedMessage.includes("please verify your email") ||
        normalizedMessage.includes("verify your email address") ||
        normalizedMessage.includes("email verification required") ||
        normalizedMessage.includes("user must verify") ||
        normalizedMessage.includes("account not verified") ||
        normalizedMessage.includes("email address not confirmed") ||
        errorCode.includes("email_not_confirmed") ||
        errorCode === "email_not_confirmed"
    ) {
        return "Please verify your email address before logging in. If you didn't receive it, you can request a new one.";
    }

    // Invalid credentials - wrong email or password
    // Note: Only check status 400 if it's NOT an email verification error (checked above)
    if (
        normalizedMessage.includes("invalid login credentials") ||
        normalizedMessage.includes("invalid email or password") ||
        normalizedMessage.includes("invalid credentials")
    ) {
        return "Incorrect email or password. Please check your credentials and try again.";
    }

    // Generic 400 status - could be invalid credentials (but not email verification, checked above)
    if (status === 400 && !normalizedMessage.includes("email") && !normalizedMessage.includes("verify")) {
        return "Incorrect email or password. Please check your credentials and try again.";
    }

    // User not found / account doesn't exist
    if (
        normalizedMessage.includes("user not found") ||
        normalizedMessage.includes("no user found") ||
        normalizedMessage.includes("user_not_found") ||
        normalizedMessage.includes("does not exist") ||
        normalizedMessage.includes("email not found")
    ) {
        return "We couldn't find an account with this email address. Please check the spelling or sign up.";
    }

    // Session errors (for password reset) - check this BEFORE account existence for reset password flow
    if (
        isPasswordReset &&
        (normalizedMessage.includes("session not found") ||
            normalizedMessage.includes("invalid session") ||
            normalizedMessage.includes("session expired") ||
            normalizedMessage.includes("token expired") ||
            normalizedMessage.includes("invalid token") ||
            normalizedMessage.includes("jwt expired") ||
            normalizedMessage.includes("jwt invalid"))
    ) {
        return "This password reset link has expired or is invalid. Please request a new password reset link.";
    }

    // Session errors (general)
    if (
        normalizedMessage.includes("session not found") ||
        normalizedMessage.includes("invalid session") ||
        normalizedMessage.includes("session expired") ||
        normalizedMessage.includes("token expired") ||
        normalizedMessage.includes("invalid token") ||
        normalizedMessage.includes("jwt expired") ||
        normalizedMessage.includes("jwt invalid")
    ) {
        return "This password reset link has expired or is invalid. Please request a new password reset link.";
    }

    // Rate limiting - too many requests
    if (
        status === 429 ||
        normalizedMessage.includes("too many requests") ||
        normalizedMessage.includes("rate limit") ||
        normalizedMessage.includes("too_many_requests")
    ) {
        return "Too many attempts. Please wait a few minutes before trying again.";
    }

    // Network/connection errors
    if (
        normalizedMessage.includes("network") ||
        normalizedMessage.includes("fetch") ||
        normalizedMessage.includes("connection") ||
        normalizedMessage.includes("timeout") ||
        normalizedMessage.includes("failed to fetch")
    ) {
        return "Unable to connect to the server. Please check your internet connection and try again.";
    }

    // Email rate limit (sending too many emails)
    if (
        normalizedMessage.includes("email rate limit") ||
        normalizedMessage.includes("email_rate_limit_exceeded") ||
        normalizedMessage.includes("too many emails")
    ) {
        return "Too many emails sent. Please wait before requesting another email.";
    }

    // Account disabled or suspended
    if (
        normalizedMessage.includes("disabled") ||
        normalizedMessage.includes("suspended") ||
        normalizedMessage.includes("banned")
    ) {
        return "This account has been disabled. Please contact support for assistance.";
    }

    // Password validation errors
    if (
        normalizedMessage.includes("password") &&
        (normalizedMessage.includes("too short") ||
            normalizedMessage.includes("too weak") ||
            normalizedMessage.includes("requirements") ||
            normalizedMessage.includes("minimum"))
    ) {
        return err.message || "Password does not meet requirements. Please ensure it's at least 6 characters long.";
    }

    // Invalid email format
    if (
        normalizedMessage.includes("invalid email") ||
        normalizedMessage.includes("email format") ||
        normalizedMessage.includes("valid email") ||
        normalizedMessage.includes("malformed")
    ) {
        return "Please enter a valid email address.";
    }

    // Server errors (5xx)
    if (status >= 500) {
        return "Server error. Please try again in a few moments.";
    }

    // Return the original error message if it's user-friendly, otherwise generic message
    if (err.message && err.message.length < 100) {
        return err.message;
    }

    return "An error occurred. Please try again later.";
};

/**
 * Determines if an account exists for a given email address
 * Used for providing better error messages without exposing account existence
 * Checks both users_profiles table and Supabase Auth to ensure consistency
 * 
 * @param {string} email - The email address to check
 * @param {Object} supabase - Supabase client instance
 * @returns {Promise<{exists: boolean, message?: string}>} - Object indicating if account exists
 */
export const checkAccountExists = async (email, supabase) => {
    if (!email || !supabase) return null;

    try {
        // First, check users_profiles table
        const { data: profileData, error: profileError } = await supabase
            .from("users_profiles")
            .select("profileid")
            .ilike("email", email)
            .maybeSingle();

        // If found in users_profiles, account definitely exists - it's a password issue
        if (profileData) {
            return {
                exists: true,
                message: "Incorrect password. If you've forgotten it, click 'Forgot Password' to reset it.",
            };
        }

        // If error is not "no rows", something went wrong
        if (profileError && profileError.code !== "PGRST116") {
            console.warn("Account lookup failed:", profileError);
            return null;
        }

        // Not found in users_profiles - check Supabase Auth directly
        // Use resetPasswordForEmail to check if account exists in Auth
        // Note: This may send a password reset email if account exists and email is confirmed
        // This is necessary to provide accurate error messages (email vs password)
        try {
            const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}/reset-password`,
            });

            // If resetPasswordForEmail succeeds (no error), account exists in Auth
            if (!resetError) {
                return {
                    exists: true,
                    message: "Incorrect password. If you've forgotten it, click 'Forgot Password' to reset it.",
                };
            }

            // Check error message to determine if account exists
            const normalizedResetError = resetError?.message?.toLowerCase() || "";
            const accountDoesNotExist = 
                normalizedResetError.includes("user not found") ||
                normalizedResetError.includes("email not found") ||
                normalizedResetError.includes("does not exist") ||
                normalizedResetError.includes("no user found");

            if (accountDoesNotExist) {
                // Account doesn't exist in Auth
                return {
                    exists: false,
                    message: "We couldn't find an account with this email address. Please check the spelling or sign up.",
                };
            }

            // Other errors (like email not confirmed) - account exists but may have issues
            // Treat as account exists (password issue)
            return {
                exists: true,
                message: "Incorrect password. If you've forgotten it, click 'Forgot Password' to reset it.",
            };
        } catch (resetError) {
            // If resetPasswordForEmail throws an exception, we can't determine account existence
            console.warn("Error checking account in Auth:", resetError);
            return null;
        }
    } catch (lookupError) {
        console.warn("Account lookup threw an exception:", lookupError);
        return null;
    }
};
